<template>
  <form @submit.prevent="handleSubmit" class="languages-form">
    <div class="form-group">
      <label :for="`lang-id-${uid}`" class="form-label">{{ t('Código (ex: en-US, pt-BR)') }} *</label>
      <input-text
        :id="`lang-id-${uid}`"
        elem-id="languages_id"
        v-model="formData.id"
        :required="true"
        :placeholder="t('fr-FR')"
        data-testid="languages-form-id"
        @blur="validateField('id')"
      />
      <span v-if="errors.id" class="error-text">{{ errors.id }}</span>
    </div>

    <div class="form-group">
      <label :for="`lang-name-${uid}`" class="form-label">{{ t('Nome') }} *</label>
      <input-text
        :id="`lang-name-${uid}`"
        elem-id="languages_name"
        v-model="formData.name"
        :required="true"
        :placeholder="t('Francês (FR)')"
        data-testid="languages-form-name"
        @blur="validateField('name')"
      />
      <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
    </div>

    <div class="form-group">
      <label :for="`lang-desc-${uid}`" class="form-label">{{ t('Descrição') }}</label>
      <input-text
        :id="`lang-desc-${uid}`"
        elem-id="languages_description"
        v-model="formData.description"
        data-testid="languages-form-description"
      />
    </div>

    <div class="form-actions">
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="loading"
        data-testid="languages-form-submit"
      >
        <span v-if="loading" class="spinner"></span>
        {{ t('Salvar') }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLanguagesAdmin } from '../../composables/useLanguagesAdmin';
import { log } from '../../utils/DebugLogger';
import InputText from '../../inputs/InputText.vue';

interface LanguageFormData {
  id: string;
  name: string;
  description: string;
}

interface FormErrors {
  id: string | null;
  name: string | null;
}

type FieldName = 'id' | 'name';

const { t } = useI18n();
const composable = useLanguagesAdmin();

const uid = Math.random().toString(36).substr(2, 9);
const emit = defineEmits<{
  'save-success': [];
}>();

const formData = ref<LanguageFormData>({
  id: '',
  name: '',
  description: ''
});

const errors = ref<FormErrors>({
  id: null,
  name: null
});

const loading = ref<boolean>(false);

const validateField = (field: FieldName): void => {
  if (field === 'id') {
    const v = formData.value.id?.trim();
    errors.value.id = v ? null : t('Código é obrigatório');
  }
  if (field === 'name') {
    const v = formData.value.name?.trim();
    errors.value.name = v ? null : t('Nome é obrigatório');
  }
};

const validateForm = (): boolean => {
  validateField('id');
  validateField('name');
  return !errors.value.id && !errors.value.name;
};

const handleSubmit = async (): Promise<void> => {
  log('LanguagesForm', 'handleSubmit', 'Submitting');

  if (!validateForm()) return;

  loading.value = true;
  try {
    await composable.create({
      id: formData.value.id.trim(),
      name: formData.value.name.trim(),
      description: formData.value.description?.trim() || ''
    });

    formData.value = { id: '', name: '', description: '' };
    errors.value = { id: null, name: null };
    emit('save-success');
  } catch (err) {
    log('LanguagesForm', 'handleSubmit', 'Error', err);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.languages-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
}

.btn:disabled {
  opacity: 0.5;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
